import LogRocket from 'logrocket';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import storageService from '../services/storage.service';
import Member from '../models/member.model';
import IAccount from '../models/account.model';
import { getLogger, registerUser, logout as logoutShipbook } from '../services/logger.service';

const logger = getLogger('Analytics');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let drift: any;

const dev = import.meta.env.MODE !== 'production';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface Window { dataLayer: any; }
}

interface NetworkRequest {
  reqId: string;
  url?: string;
  headers: { [key: string]: string | null | undefined };
  body?: string;
}

interface NetworkResponse {
  reqId: string;
  url?: string;
  body?: string;
}

const passwordUrls = ['auth/register', 'auth/login', 'auth/changePassword'];
const tokenUrls = ['auth/register', 'auth/login', 'auth/refreshToken', 'auth/token'];
// LogRocket 12 response objects may omit `url`, so auth requests are remembered by reqId
const authRequestUrls = new Map<string, string>();

export function requestSanitizer<T extends NetworkRequest>(request: T): T | null {
  try {
    const url = request.url ?? '';
    if (passwordUrls.concat(tokenUrls).some(value => url.includes(value))) {
      authRequestUrls.set(request.reqId, url);
    }
    if (request.body && passwordUrls.some(value => url.includes(value))) {
      try {
        const bodyObj = JSON.parse(request.body);
        if (bodyObj?.password) {
          bodyObj.password = '';
          request.body = JSON.stringify(bodyObj);
        }
      }
      catch (e) {
        logger.e("There was an error in parsing request body", e);
      }
    }

    // set empty authorization header
    if (request.headers["authorization"]) request.headers["authorization"] = '';
    return request;
  }
  catch (e) {
    // a sanitizer exception escapes into LogRocket's fetch hook and breaks the page's network calls
    logger.e("requestSanitizer failed, dropping request from recording", e);
    return null;
  }
}

export function responseSanitizer<T extends NetworkResponse>(response: T): T | null {
  try {
    const url = response.url ?? authRequestUrls.get(response.reqId) ?? '';
    authRequestUrls.delete(response.reqId);
    if (response.body && tokenUrls.some(value => url.includes(value))) {
      try {
        const bodyObj = JSON.parse(response.body);
        if (bodyObj?.token) {
          bodyObj.token = '';
          response.body = JSON.stringify(bodyObj);
        }
      }
      catch (e) {
        logger.e("There was an error in parsing reponse body", e);
      }
    }
    return response;
  }
  catch (e) {
    logger.e("responseSanitizer failed, dropping response from recording", e);
    return null;
  }
}

if (!dev) {
  logger.i('It is production');
  window.dataLayer = window.dataLayer || [];

  // initializing logrocket
  LogRocket.init('mcvb4w/shipbook-console', {
    network: {
      requestSanitizer,
      responseSanitizer,
    },
  });
}

interface Props extends RouteComponentProps<{}> {
}

export class Analytics extends React.Component<Props> {  
  componentDidUpdate(prevProps: Props) {
    const currentPage = this.props.location.pathname;
    const prevPage = prevProps.location.pathname;
    if (currentPage !== prevPage) trackAnalytic.trackPage(currentPage);
  }

  render() {
    return null;
    // return <React.Component {...this.props} />;
  }
}

class TrackAnalytic {
  openChat() { // not the right place but didn't find a better place
    drift.api.openChat();
  }
  trackPage(pathname: string, search: string = '') {
    const page = pathname + search;
    logger.d('called trackPage with page:' + page);
    if (!dev) {
      // TODO: add if needed track page to google analytics
    }
  }

  user(member: Member, account?: IAccount) {
    logger.d('called trackUser with user:' + member._id);

    // adding info to Shipbook
    registerUser(member._id, member.fullName, member.fullName, member.email, member.phone);

    if (dev) return;
    // adding to google
    // sending user id
    window.dataLayer.push({
      userId: member._id,
      event: 'userId'
    });

    // adding info to drift
    drift.identify(member._id, {
      email: member.email,
      name: member.fullName,
      phone: member.phone,
      company: account?.name,
      address: account?.address?.label
    });

    // adding info into Logrocket
    LogRocket.identify(member._id, {
      email: member.email,
      name: member.fullName,
    });

  }
  
  register(userId: string) {
    logger.d('called trackRegister with user:' + userId);
    if (dev) return;
    LogRocket.track('registration');
    // using user id as transaction id
    // sending user register
    window.dataLayer.push({
      userId,
      event: 'registration'
    });

    storageService.setItem('conversionTracking', true);
  }

  integration(userId: string) {
    logger.d('called trackIntegration with user:' + userId);
    if (dev) return;

    if (!storageService.getItem('conversionTracking', false)) return;
    LogRocket.track('integration');
    // using user id as transaction id
    // sending user integration
    window.dataLayer.push({
      userId,
      event: 'integration'
    });

    storageService.removeItem('conversionTracking')
  }
   
  event(category: string, action: string, label: string) {
    if (dev) return;
    window.dataLayer.push({
      event: action,
      event_category: category,
      event_label: label
    });
  }

  logout() {
    logger.d('called logout');
    logoutShipbook();
  }
}

const trackAnalytic = new TrackAnalytic();
export default trackAnalytic;
